'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Utensils, Bug, Leaf, Filter, Search, X, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { feedingService, harvestService, populationService } from '@/lib/services/farmService';

type ActivityType = 'feeding' | 'health' | 'harvest';

interface ActivityItem {
  id: string;
  type: ActivityType;
  user: string;
  userInitials: string;
  bedId: string;
  detail: string;
  subDetail: string;
  timestamp: string;
  relTime: string;
  date: string;
}

const typeConfig: Record<ActivityType, { label: string; icon: React.ElementType; color: string; bg: string; badgeBg: string; badgeText: string }> = {
  feeding: { label: 'Feeding', icon: Utensils, color: 'text-success', bg: 'bg-success-bg', badgeBg: 'bg-success-bg', badgeText: 'text-success' },
  health: { label: 'Health Check', icon: Bug, color: 'text-info', bg: 'bg-info-bg', badgeBg: 'bg-info-bg', badgeText: 'text-info' },
  harvest: { label: 'Harvest', icon: Leaf, color: 'text-primary', bg: 'bg-secondary', badgeBg: 'bg-secondary', badgeText: 'text-secondary-foreground' },
};

const allTypes: ('all' | ActivityType)[] = ['all', 'feeding', 'health', 'harvest'];

function initials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function relativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(isoString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function ActivityFeedScreen() {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [liveIndicator, setLiveIndicator] = useState(false);
  const [typeFilter, setTypeFilter] = useState<'all' | ActivityType>('all');
  const [userFilter, setUserFilter] = useState('All Workers');
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const buildActivities = useCallback(async (): Promise<ActivityItem[]> => {
    const [feedings, harvests, healthChecks] = await Promise.all([
      feedingService.getAll(),
      harvestService.getAll(),
      populationService.getAll(),
    ]);

    const items: ActivityItem[] = [];

    feedings.forEach((f) => {
      const name = f.loggedBy ?? 'Unknown';
      const ts = f.createdAt;
      items.push({
        id: `feeding-${f.id}`,
        type: 'feeding',
        user: name,
        userInitials: initials(name),
        bedId: f.bedId,
        detail: `${f.feedType} — ${f.feedQuantityKg} kg`,
        subDetail: [
          f.phReading != null ? `pH ${f.phReading}` : null,
          f.moistureLevel ? `Moisture ${f.moistureLevel}` : null,
          f.beddingMaterial ? `Bedding: ${f.beddingMaterial}` : null,
        ]
          .filter(Boolean)
          .join(' · ') || 'No additional details',
        timestamp: ts,
        relTime: relativeTime(ts),
        date: formatDate(f.feedingDate + 'T00:00:00'),
      });
    });

    harvests.forEach((h) => {
      const name = h.loggedBy ?? 'Unknown';
      const ts = h.createdAt;
      items.push({
        id: `harvest-${h.id}`,
        type: 'harvest',
        user: name,
        userInitials: initials(name),
        bedId: h.bedId,
        detail: `Harvest ${h.wetWeightKg} kg wet — Grade ${h.qualityGrade}`,
        subDetail: [
          h.dryWeightKg != null ? `Dry ${h.dryWeightKg} kg` : null,
          h.volumeL != null ? `Vol ${h.volumeL} L` : null,
          h.moisturePct != null ? `Moisture ${h.moisturePct}%` : null,
        ]
          .filter(Boolean)
          .join(' · ') || 'No additional details',
        timestamp: ts,
        relTime: relativeTime(ts),
        date: formatDate(h.harvestDate + 'T00:00:00'),
      });
    });

    healthChecks.forEach((p) => {
      const name = p.loggedBy ?? 'Unknown';
      const ts = p.createdAt;
      const scoreLabel = p.healthScore >= 8 ? 'excellent' : p.healthScore >= 6 ? 'good' : p.healthScore >= 4 ? 'fair' : 'flagged';
      items.push({
        id: `health-${p.id}`,
        type: 'health',
        user: name,
        userInitials: initials(name),
        bedId: p.bedId,
        detail: `Health score ${p.healthScore}/10 — ${scoreLabel}`,
        subDetail: [
          p.temperatureC != null ? `Temp ${p.temperatureC}°C` : null,
          p.moisturePct != null ? `Moisture ${p.moisturePct}%` : null,
          p.adultCount != null ? `Adults ${p.adultCount.toLocaleString()}` : null,
        ]
          .filter(Boolean)
          .join(' · ') || 'No additional details',
        timestamp: ts,
        relTime: relativeTime(ts),
        date: formatDate(p.checkDate + 'T00:00:00'),
      });
    });

    items.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return items;
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const items = await buildActivities();
      setActivities(items);
    } finally {
      setLoading(false);
    }
  }, [buildActivities]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Real-time subscriptions
  useEffect(() => {
    const supabase = createClient();

    const flashLive = () => {
      setLiveIndicator(true);
      setTimeout(() => setLiveIndicator(false), 1500);
    };

    const handleChange = async () => {
      flashLive();
      const items = await buildActivities();
      setActivities(items);
    };

    const feedingChannel = supabase
      .channel('rt-feeding-logs')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'feeding_logs' }, handleChange)
      .subscribe();

    const harvestChannel = supabase
      .channel('rt-harvest-records')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'harvest_records' }, handleChange)
      .subscribe();

    const healthChannel = supabase
      .channel('rt-population-checks')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'population_checks' }, handleChange)
      .subscribe();

    return () => {
      supabase.removeChannel(feedingChannel);
      supabase.removeChannel(harvestChannel);
      supabase.removeChannel(healthChannel);
    };
  }, [buildActivities]);

  const allUsers = ['All Workers', ...Array.from(new Set(activities.map((a) => a.user))).sort()];

  const filtered = activities.filter((a) => {
    const matchType = typeFilter === 'all' || a.type === typeFilter;
    const matchUser = userFilter === 'All Workers' || a.user === userFilter;
    const matchSearch =
      search === '' ||
      a.bedId.toLowerCase().includes(search.toLowerCase()) ||
      a.user.toLowerCase().includes(search.toLowerCase()) ||
      a.detail.toLowerCase().includes(search.toLowerCase());
    return matchType && matchUser && matchSearch;
  });

  const grouped = filtered.reduce<Record<string, ActivityItem[]>>((acc, item) => {
    if (!acc[item.date]) acc[item.date] = [];
    acc[item.date].push(item);
    return acc;
  }, {});

  const activeFilterCount = (typeFilter !== 'all' ? 1 : 0) + (userFilter !== 'All Workers' ? 1 : 0);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-foreground">Activity Feed</h1>
          <p className="text-sm text-muted-foreground mt-0.5">All farm operations across all workers</p>
        </div>
        <div className="flex items-center gap-1.5 mt-1">
          <span className={`w-2 h-2 rounded-full transition-colors ${liveIndicator ? 'bg-success animate-pulse' : 'bg-success/60'}`} />
          <span className="text-xs text-muted-foreground font-medium">Live</span>
        </div>
      </div>

      {/* Search + Filter bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search bed, worker, or activity…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2.5 text-sm rounded-xl border border-border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
              <X size={13} />
            </button>
          )}
        </div>
        <button
          onClick={() => setShowFilters((v) => !v)}
          className={`relative flex items-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
            showFilters || activeFilterCount > 0
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-card border-border text-foreground hover:bg-muted'
          }`}
        >
          <Filter size={14} />
          Filters
          {activeFilterCount > 0 && (
            <span className="w-4 h-4 rounded-full bg-primary-foreground text-primary text-[10px] font-bold flex items-center justify-center leading-none">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="bg-card rounded-xl border border-border p-4 space-y-4">
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Activity Type</p>
            <div className="flex flex-wrap gap-2">
              {allTypes.map((t) => (
                <button
                  key={`type-chip-${t}`}
                  onClick={() => setTypeFilter(t)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    typeFilter === t
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-muted text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                  }`}
                >
                  {t === 'all' ? 'All Types' : typeConfig[t].label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Worker</p>
            <div className="flex flex-wrap gap-2">
              {allUsers.map((u) => (
                <button
                  key={`user-chip-${u}`}
                  onClick={() => setUserFilter(u)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    userFilter === u
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-muted text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                  }`}
                >
                  {u}
                </button>
              ))}
            </div>
          </div>

          {activeFilterCount > 0 && (
            <button
              onClick={() => { setTypeFilter('all'); setUserFilter('All Workers'); }}
              className="text-xs text-danger font-medium hover:underline"
            >
              Clear all filters
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={28} className="animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Results count */}
          <p className="text-xs text-muted-foreground px-1">
            Showing <span className="font-semibold text-foreground">{filtered.length}</span> of {activities.length} activities
          </p>

          {/* Grouped feed */}
          {Object.keys(grouped).length === 0 ? (
            <div className="bg-card rounded-xl border border-border p-8 text-center">
              <Search className="mx-auto mb-3 text-muted-foreground" size={32} />
              <p className="text-sm font-semibold text-foreground">No activities match your filters</p>
              <p className="text-xs text-muted-foreground mt-1">Try adjusting the type or worker filter above</p>
            </div>
          ) : (
            Object.entries(grouped).map(([date, items]) => (
              <div key={`group-${date}`}>
                <div className="flex items-center gap-2 mb-2 px-1">
                  <span className="text-xs font-bold text-muted-foreground uppercase tracking-wide">{date}</span>
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground">{items.length} {items.length === 1 ? 'activity' : 'activities'}</span>
                </div>

                <div className="space-y-2">
                  {items.map((item) => {
                    const cfg = typeConfig[item.type];
                    const ItemIcon = cfg.icon;
                    return (
                      <div
                        key={item.id}
                        className="bg-card rounded-xl border border-border p-4 flex gap-3 hover:bg-muted/30 transition-colors"
                      >
                        {/* User avatar */}
                        <div className="flex-shrink-0 flex flex-col items-center gap-1.5">
                          <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold">
                            {item.userInitials}
                          </div>
                          <div className={`w-6 h-6 rounded-full ${cfg.bg} flex items-center justify-center`}>
                            <ItemIcon size={12} className={cfg.color} />
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-foreground">{item.user}</span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${cfg.badgeBg} ${cfg.badgeText}`}>
                                {cfg.label}
                              </span>
                              <span className="text-xs font-semibold text-primary bg-secondary px-2 py-0.5 rounded-full">
                                {item.bedId}
                              </span>
                            </div>
                            <span className="text-xs text-muted-foreground flex-shrink-0">{item.relTime}</span>
                          </div>

                          <p className="text-sm text-foreground font-medium">{item.detail}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{item.subDetail}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
}